import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Label, Textarea } from "@cieda/djayma-ui";
import { useStockAdjustment } from "@/hooks/admin/useStockAdjustment";
import { ConflictResolutionDialog } from "./ConflictResolutionDialog";

type Direction = "reception" | "sortie" | "correction";
type Reason = "restock" | "sale" | "return" | "adjustment";

interface StockAdjustmentFormProps {
  readonly productId: string;
  readonly currentStock: number;
  readonly currentVersion: number;
}

interface ConflictState {
  readonly currentStock: number;
  readonly yourDelta: number;
}

/**
 * +/-/correction form with live preview and optimistic-concurrency conflict handling.
 * On 409 responses the mutation throws — we show ConflictResolutionDialog with a 3-way merge.
 */
export function StockAdjustmentForm({
  productId,
  currentStock,
  currentVersion,
}: StockAdjustmentFormProps) {
  const { t } = useTranslation("admin");
  const mutation = useStockAdjustment(productId);

  const [direction, setDirection] = useState<Direction>("reception");
  const [quantity, setQuantity] = useState<string>("");
  const [reason, setReason] = useState<Reason>("restock");
  const [notes, setNotes] = useState("");
  const [conflictData, setConflictData] = useState<ConflictState | null>(null);

  const delta = useMemo(() => {
    const qty = parseInt(quantity, 10);
    if (Number.isNaN(qty) || qty === 0) return 0;
    switch (direction) {
      case "reception":
        return Math.abs(qty);
      case "sortie":
        return -Math.abs(qty);
      case "correction":
        // User may type signed value; we preserve sign as-is for correction mode.
        return qty;
    }
  }, [direction, quantity]);

  const newStock = currentStock + delta;
  const isNotesRequired = reason === "adjustment";
  const isInsufficient = newStock < 0;
  const isValid =
    delta !== 0 &&
    !isInsufficient &&
    (!isNotesRequired || notes.trim().length > 0);

  const handleSubmit = useCallback(async () => {
    if (!isValid) return;
    try {
      await mutation.mutateAsync({
        delta,
        reason,
        notes: notes.trim() || null,
        expected_version: currentVersion,
      });
      setQuantity("");
      setNotes("");
    } catch (error: unknown) {
      if (error instanceof Error && error.message.includes("409")) {
        setConflictData({ currentStock: newStock, yourDelta: delta });
      }
      // Non-409 errors are surfaced via the hook's toast.error handler.
    }
  }, [
    isValid,
    mutation,
    delta,
    reason,
    notes,
    currentVersion,
    newStock,
  ]);

  const directionOptions: ReadonlyArray<readonly [Direction, string]> = [
    ["reception", t("products.stock.direction_reception")],
    ["sortie", t("products.stock.direction_sortie")],
    ["correction", t("products.stock.direction_correction")],
  ];

  const reasonOptions: ReadonlyArray<readonly [Reason, string]> = [
    ["restock", t("products.stock.reason_restock")],
    ["sale", t("products.stock.reason_sale")],
    ["return", t("products.stock.reason_return")],
    ["adjustment", t("products.stock.reason_adjustment")],
  ];

  return (
    <div className="space-y-4">
      {/* Direction radio group */}
      <fieldset>
        <legend className="sr-only">
          {t("products.stock.direction_reception")}
        </legend>
        <div className="flex gap-2">
          {directionOptions.map(([value, label]) => {
            const isActive = direction === value;
            return (
              <label
                key={value}
                className={`flex-1 cursor-pointer rounded-md border px-3 py-2 text-center text-sm transition-colors ${
                  isActive
                    ? "border-amber-600 bg-amber-50 font-medium text-amber-900"
                    : "border-stone-200 text-stone-600 hover:bg-stone-50"
                }`}
              >
                <input
                  type="radio"
                  name="direction"
                  value={value}
                  checked={isActive}
                  onChange={() => setDirection(value)}
                  className="sr-only"
                  aria-label={label}
                />
                {label}
              </label>
            );
          })}
        </div>
      </fieldset>

      {/* Quantity */}
      <div className="space-y-1">
        <Label htmlFor="stock-qty">{t("products.stock.quantity")}</Label>
        <Input
          id="stock-qty"
          type="number"
          min={direction === "correction" ? undefined : 1}
          inputMode="numeric"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder="0"
          aria-describedby={isInsufficient ? "stock-error" : undefined}
          aria-invalid={isInsufficient}
        />
      </div>

      {/* Reason */}
      <div className="space-y-1">
        <Label htmlFor="stock-reason">{t("products.stock.reason")}</Label>
        <select
          id="stock-reason"
          className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-amber-600 focus:outline-none focus:ring-1 focus:ring-amber-600"
          value={reason}
          onChange={(e) => setReason(e.target.value as Reason)}
        >
          {reasonOptions.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Notes */}
      <div className="space-y-1">
        <Label htmlFor="stock-notes">
          {t("products.stock.notes")}
          {isNotesRequired && (
            <span className="ml-1 text-xs text-red-500" aria-hidden="true">
              *
            </span>
          )}
        </Label>
        <Textarea
          id="stock-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={
            isNotesRequired ? t("products.stock.notes_required") : ""
          }
          rows={2}
          required={isNotesRequired}
        />
      </div>

      {/* Live preview */}
      {delta !== 0 && (
        <div
          className="rounded-md bg-stone-50 px-3 py-2 text-sm tabular-nums text-stone-700"
          aria-live="polite"
        >
          {currentStock.toLocaleString("fr-SN")} →{" "}
          <span
            className={
              isInsufficient ? "font-medium text-red-600" : "font-medium"
            }
          >
            {newStock.toLocaleString("fr-SN")}
          </span>{" "}
          <span className={delta > 0 ? "text-emerald-600" : "text-red-600"}>
            ({delta > 0 ? "+" : ""}
            {delta.toLocaleString("fr-SN")})
          </span>
        </div>
      )}

      {isInsufficient && (
        <p id="stock-error" className="text-sm text-red-600">
          {t("products.stock.error_insufficient")}
        </p>
      )}

      {/* Submit */}
      <Button
        className="w-full"
        disabled={!isValid || mutation.isPending}
        onClick={handleSubmit}
        type="button"
      >
        {mutation.isPending ? "…" : t("products.stock.confirm")}
      </Button>

      {/* Conflict dialog */}
      {conflictData && (
        <ConflictResolutionDialog
          open={Boolean(conflictData)}
          onOpenChange={(open) => {
            if (!open) setConflictData(null);
          }}
          originalStock={currentStock}
          serverStock={conflictData.currentStock}
          yourDelta={conflictData.yourDelta}
          onResolve={(choice) => {
            setConflictData(null);
            // The parent StockDrawer will receive refetched data with new version;
            // the user can manually re-submit. "apply" and "keep" both close the dialog
            // and let the user decide the next action deliberately.
            if (choice === "cancel") {
              setQuantity("");
              setNotes("");
            }
          }}
        />
      )}
    </div>
  );
}
